/**
 * useShare Hook for Capacitor
 *
 * Provides sharing functionality that works on both native (iOS/Android)
 * and web platforms using the Web Share API.
 */

import { useState, useCallback } from 'react';
import { Share, ShareOptions, ShareResult } from '@capacitor/share';
import { isNative, isPluginAvailable } from '@/utils/platform';

export interface ShareData {
  title?: string;
  text?: string;
  url?: string;
  dialogTitle?: string;
  files?: File[];
}

export interface UseShareReturn {
  share: (data: ShareData) => Promise<boolean>;
  canShare: boolean;
  isSharing: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Check if sharing is supported on this platform
 */
function checkShareSupport(): boolean {
  if (isNative && isPluginAvailable('Share')) {
    return true;
  }

  // Check for Web Share API support
  return 'share' in navigator || 'canShare' in navigator;
}

export function useShare(): UseShareReturn {
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canShare = checkShareSupport();

  const clearError = useCallback(() => setError(null), []);

  /**
   * Share content using native or web share
   */
  const share = useCallback(async (data: ShareData): Promise<boolean> => {
    if (!canShare) {
      setError('Sharing is not supported on this device');
      return false;
    }

    setIsSharing(true);
    setError(null);

    try {
      if (isNative && isPluginAvailable('Share')) {
        // Use native Capacitor share
        const shareOptions: ShareOptions = {
          title: data.title,
          text: data.text,
          url: data.url,
          dialogTitle: data.dialogTitle,
        };

        // Handle file sharing if files are provided
        if (data.files && data.files.length > 0) {
          // Convert files to data URLs for native sharing
          const filePromises = data.files.map(async (file) => {
            return new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          });

          const fileDataUrls = await Promise.all(filePromises);
          (shareOptions as any).files = fileDataUrls;
        }

        const result: ShareResult = await Share.share(shareOptions);

        // Check if share was successful (not cancelled)
        if (result.activityType) {
          console.log('[Share] Shared via:', result.activityType);
        }

        return true;
      } else if ('share' in navigator) {
        // Use Web Share API
        const webShareData: ShareData = {
          title: data.title,
          text: data.text,
          url: data.url,
        };

        // Handle file sharing for web
        if (data.files && data.files.length > 0 && 'canShare' in navigator) {
          const shareData = { ...webShareData, files: data.files };
          if (navigator.canShare(shareData as any)) {
            await navigator.share(shareData as any);
            return true;
          }
        }

        await navigator.share(webShareData as any);
        return true;
      } else {
        // Fallback: copy to clipboard
        const textToShare = data.url || data.text || data.title || '';
        await navigator.clipboard.writeText(textToShare);
        console.log('[Share] Copied to clipboard (fallback)');
        return true;
      }
    } catch (err: any) {
      // User cancelled sharing - not an error
      if (err.name === 'AbortError' || err.message?.includes('cancelled')) {
        return false;
      }

      const errorMessage = err.message || 'Failed to share';
      setError(errorMessage);
      console.error('[Share] Error:', err);
      return false;
    } finally {
      setIsSharing(false);
    }
  }, [canShare]);

  return {
    share,
    canShare,
    isSharing,
    error,
    clearError,
  };
}

/**
 * Quick share utilities
 */
export const shareUtils = {
  /**
   * Share a URL with title
   */
  shareUrl: async (url: string, title?: string): Promise<boolean> => {
    const { share } = useShare();
    return share({ url, title });
  },

  /**
   * Share text content
   */
  shareText: async (text: string, title?: string): Promise<boolean> => {
    const { share } = useShare();
    return share({ text, title });
  },

  /**
   * Share a referral link
   */
  shareReferral: async (referralCode: string, userName: string): Promise<boolean> => {
    const { share } = useShare();
    const url = `https://gotradeloop.com/join?ref=${referralCode}`;
    const text = `${userName} invited you to join Trade Loop - the professional referral network for trades!`;

    return share({
      title: 'Join Trade Loop',
      text,
      url,
      dialogTitle: 'Share your referral link',
    });
  },
};
